<script lang="ts" setup>
import { ComboboxAnchor, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxRoot, ComboboxTrigger, ComboboxViewport } from 'radix-vue'
import { useFuse } from '@vueuse/integrations/useFuse'
import type { InputOption } from '../types'
import { ref, computed, watch } from '#imports'

type OptionItem = InputOption<string>

interface YscComboboxProps {
  inputId: string
  options: OptionItem[]
  disabled?: boolean
  required?: boolean
  acceptCustomValue?: boolean
  fullWidth?: boolean
  multiple?: boolean
  placeholder?: string
}

const props = withDefaults(defineProps<YscComboboxProps>(), {
  fullWidth: false,
  multiple: false,
})

const options = computed(() => [...props.options])

const modelValue = defineModel<string | string[]>()

const customValueOption = computed(() => {
  if (props.acceptCustomValue && searchTerm.value) {
    return {
      text: searchTerm.value,
      value: searchTerm.value,
    }
  }
  return undefined
})

const searchTerm = ref('') // Binded to Radix combobox search term
const isOpen = ref(false)
const fuseSearchTerm = ref('')
// Update to searchTerm value when it's updated. Will be reset on opening the combobox so all options are visible until user actively search for something.
const { results } = useFuse(fuseSearchTerm, options, { fuseOptions: { keys: ['text', 'value'] }, matchAllWhenSearchEmpty: true })

const filterFunction = (list: string[]) => { // Have to provide a filter function otherwise radix is going to do its own filtering in addition to ours.
  return list
}

watch(isOpen, (newValue, oldValue) => {
  if (!oldValue && newValue) {
    fuseSearchTerm.value = ''
  }
})

const onUpdateSearchTerm = (term: string) => {
  fuseSearchTerm.value = term
}

const displayValue = (selectedValue: string) => {
  return options.value.find(({ value }) => selectedValue === value)?.text ?? (props.acceptCustomValue ? selectedValue : '')
}

const onInputBlur = () => {
  if (!props.acceptCustomValue) return // keep the normal radix behaviour if we don't accept custom values

  const exactMatch = props.options.find(({ text }) => text === searchTerm.value)

  if (exactMatch) {
    modelValue.value = exactMatch.value
    return
  }
  modelValue.value = searchTerm.value
  return
}
</script>

<template>
  <ComboboxRoot
    v-model="modelValue"
    v-model:search-term="searchTerm"
    v-model:open="isOpen"
    class="relative"
    :display-value="displayValue"
    :disabled="disabled"
    :filter-function="filterFunction"
    :multiple="multiple"
    :reset-search-term-on-blur="!acceptCustomValue"
    @update:search-term="onUpdateSearchTerm"
  >
    <ComboboxAnchor
      class="yscComboboxAnchor"
      :class="{
        'w-full': fullWidth,
        'w-max': !fullWidth,
      }"
    >
      <ComboboxInput
        ref="comboboxInput"
        class="bg-transparent flex-1 outline-none"
        :placeholder="placeholder"
        :required="required"
        @blur="onInputBlur"
      />
      <ComboboxTrigger>
        <YscIconsArrowBottom
          class="h-4 w-4 text-gray-400"
          :class="{ 'rotate-180': isOpen }"
        />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxContent
      class="absolute z-20 rounded-sm border border-gray-300 bg-white mt-2"
      style="width: var(--radix-combobox-trigger-width)"
      position="popper"
      side="bottom"
      align="start"
    >
      <ComboboxViewport
        class="w-full overflow-y-auto flex flex-col gap-2 py-1 h-viewport max-h-52"
      >
        <ComboboxEmpty class="justify-center flex text-gray-400">
          {{ $t('commons.no_results') }}
        </ComboboxEmpty>
        <ComboboxItem
          v-if="customValueOption"
          :value="customValueOption.value"
          class="hidden"
        >
          <span
            class="pointer-events-none block truncate"
          >{{ customValueOption.text }}</span>
          <YscIconsCheckCircle
            class="h-4 w-4 text-black iconChecked hidden"
            aria-hidden
          />
          <YscIconsCheckEmpty
            class="h-4 w-4 iconUnchecked"
            aria-hidden
          />
        </ComboboxItem>
        <ComboboxItem
          v-for="opt in results"
          :key="opt.item.value"
          :value="opt.item.value"
          :disabled="opt.item.disabled"
          class="yscComboboxOption"
        >
          <span
            class="pointer-events-none block truncate"
          >{{ opt.item.text }}</span>
          <YscIconsCheckCircle
            class="h-4 w-4 text-black iconChecked hidden"
            aria-hidden
          />
          <YscIconsCheckEmpty
            class="h-4 w-4 iconUnchecked"
            aria-hidden
          />
        </ComboboxItem>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
</template>

<style scoped>
.yscComboboxOption{@apply flex flex-row justify-between items-center cursor-pointer text-gray-400 py-2 px-4 gap-2}.yscComboboxOption[data-disabled]{@apply opacity-50 cursor-not-allowed text-gray-300}.yscComboboxOption[data-state=checked]{@apply border-l-2 border-peacock-200 bg-gray-100}.yscComboboxOption[data-state=checked]>.iconChecked{display:block}.yscComboboxOption[data-state=checked]>.iconUnchecked{display:none}.yscComboboxOption[data-highlighted]{@apply bg-gray-100 border-x border-gray-400 border-collapse}.yscComboboxAnchor{@apply flex flex-row gap-2 border border-gray-300 rounded-sm py-3 px-4}.yscComboboxAnchor:has(input:focus-visible){@apply ring-2 ring-peacock-300 border-peacock-300}.yscComboboxAnchor:has(input:disabled){@apply bg-gray-100 border-gray-200 text-gray-300}
</style>
